import React, { useState, useEffect } from 'react';
import { displayAPI, scheduleAPI, settingsAPI } from '../api/endpoints';
import './Dashboard.css';

interface Schedule {
  _id: string;
  name: string;
}

interface Display {
  _id: string;
  name: string;
  location: string;
  status: 'online' | 'offline' | 'error';
  lastSeen: string;
  lastScreenshotAt?: string | null;
  resolution: { width: number; height: number };
  scaling: number;
  settings: { brightness: number; orientation: string };
  currentSchedule?: Schedule | null;
}

// Absolute origin for /uploads assets (screenshots). Relative /api in production
// → page origin; absolute dev URL → its origin.
const rawApiUrl = process.env.REACT_APP_API_URL || '/api';
const uploadsOrigin = /^https?:\/\//i.test(rawApiUrl)
  ? rawApiUrl.replace(/\/api\/?$/, '')
  : window.location.origin;

const emptyForm = {
  name: '',
  location: '',
  width: 1920,
  height: 1080,
  scaling: 1,
  orientation: 'landscape',
};

export const Dashboard: React.FC = () => {
  const [displays, setDisplays] = useState<Display[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [displayBase, setDisplayBase] = useState<string>('');

  useEffect(() => {
    fetchData();
    settingsAPI.getPublic().then((r) => {
      const { publicBaseUrl, publicDisplayPath } = r.data.data || {};
      const origin = (publicBaseUrl || window.location.origin).replace(/\/$/, '');
      const path = publicDisplayPath || '/display';
      setDisplayBase(origin + path);
    }).catch(() => {
      setDisplayBase(window.location.origin + '/display');
    });
    // Refresh displays periodically so new monitoring screenshots appear on their own.
    const interval = setInterval(() => { fetchData(); }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayUrl = (id: string) => `${displayBase}/?display=${id}`;

  const fetchData = async () => {
    try {
      const [dispRes, schedRes] = await Promise.all([
        displayAPI.getAll(),
        scheduleAPI.getAll(),
      ]);
      setDisplays(dispRes.data.data);
      setSchedules(schedRes.data.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSchedule = async (displayId: string, scheduleId: string) => {
    try {
      if (scheduleId === '') {
        await displayAPI.removeSchedule(displayId);
      } else {
        await displayAPI.assignSchedule(displayId, scheduleId);
      }
      fetchData();
    } catch (error) {
      alert('Fehler bei der Zuweisung');
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (d: Display) => {
    setEditId(d._id);
    setForm({
      name: d.name,
      location: d.location || '',
      width: d.resolution?.width || 1920,
      height: d.resolution?.height || 1080,
      scaling: d.scaling ?? 1,
      orientation: d.settings?.orientation || 'landscape',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      location: form.location,
      resolution: { width: form.width, height: form.height },
      scaling: form.scaling,
      settings: { orientation: form.orientation },
    };
    try {
      if (editId) {
        await displayAPI.update(editId, payload);
      } else {
        await displayAPI.create(payload);
      }
      setShowForm(false);
      fetchData();
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Fehler beim Speichern';
      alert(msg);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Display wirklich löschen?')) return;
    try {
      await displayAPI.delete(id);
      fetchData();
    } catch (error) {
      alert('Fehler beim Löschen');
    }
  };

  const copyUrl = (id: string) => {
    navigator.clipboard.writeText(displayUrl(id));
    alert('URL kopiert!');
  };

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      
      <div className="dashboard-grid">
        <div className="card">
          <h3>Displays</h3>
          <p className="stat">{displays.length}</p>
        </div>
        <div className="card">
          <h3>Online</h3>
          <p className="stat online">
            {displays.filter(d => d.status === 'online').length}
          </p>
        </div>
        <div className="card">
          <h3>Offline</h3>
          <p className="stat offline">
            {displays.filter(d => d.status === 'offline').length}
          </p>
        </div>
        <div className="card">
          <h3>Schedules</h3>
          <p className="stat">{schedules.length}</p>
        </div>
      </div>

      <div className="displays-section">
        <div className="section-header">
          <h2>Displays</h2>
          <button className="btn-create" onClick={openCreate}>+ Neues Display</button>
        </div>

        {showForm && (
          <div className="display-form">
            <h3>{editId ? 'Display bearbeiten' : 'Neues Display anlegen'}</h3>
            <div className="form-grid">
              <label>
                Name
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="z.B. Empfang Links" />
              </label>
              <label>
                Standort
                <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="z.B. Gebäude A, EG" />
              </label>
              <label>
                Breite (px)
                <input type="number" value={form.width} onChange={e => setForm({ ...form, width: Number(e.target.value) })} />
              </label>
              <label>
                Höhe (px)
                <input type="number" value={form.height} onChange={e => setForm({ ...form, height: Number(e.target.value) })} />
              </label>
              <label>
                Skalierung
                <input type="number" step="0.1" min="0.1" max="5" value={form.scaling} onChange={e => setForm({ ...form, scaling: Number(e.target.value) })} />
              </label>
              <label>
                Orientierung
                <select value={form.orientation} onChange={e => setForm({ ...form, orientation: e.target.value })}>
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
              </label>
            </div>
            <div className="form-actions">
              <button className="btn-save" onClick={handleSave}>{editId ? 'Speichern' : 'Anlegen'}</button>
              <button className="btn-cancel" onClick={() => setShowForm(false)}>Abbrechen</button>
            </div>
          </div>
        )}

        {loading ? (
          <p>Laden...</p>
        ) : displays.length === 0 && !showForm ? (
          <p className="empty-hint">Noch keine Displays angelegt. Klicke auf "+ Neues Display".</p>
        ) : (
          <div className="displays-list">
            {displays.map(display => (
              <div key={display._id} className="display-card">
                <div className="display-card-header">
                  <h3>{display.name}</h3>
                  <div className="display-card-actions">
                    <button className="btn-icon" title="Bearbeiten" onClick={() => openEdit(display)}>✏️</button>
                    <button className="btn-icon" title="Löschen" onClick={() => handleDelete(display._id)}>🗑️</button>
                  </div>
                </div>
                <div className="display-screenshot">
                  {display.lastScreenshotAt ? (
                    <>
                      <img
                        src={`${uploadsOrigin}/uploads/screenshots/${display._id}.png?t=${new Date(display.lastScreenshotAt).getTime()}`}
                        alt={`Screenshot ${display.name}`}
                        loading="lazy"
                      />
                      <span className="screenshot-time">
                        📸 {new Date(display.lastScreenshotAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </>
                  ) : (
                    <div className="screenshot-empty">Kein Screenshot</div>
                  )}
                </div>
                {display.location && <p><strong>Standort:</strong> {display.location}</p>}
                <p><strong>Auflösung:</strong> {display.resolution?.width || 1920} × {display.resolution?.height || 1080}</p>
                <p><strong>Skalierung:</strong> {display.scaling ?? 1}×</p>
                <p><strong>Orientierung:</strong> {display.settings?.orientation || 'landscape'}</p>
                <p><strong>Status:</strong> <span className={`status ${display.status}`}>{display.status}</span></p>
                
                <div className="schedule-assign">
                  <label><strong>Schedule:</strong></label>
                  <select
                    value={display.currentSchedule?._id || ''}
                    onChange={(e) => handleAssignSchedule(display._id, e.target.value)}
                  >
                    <option value="">— Kein Schedule —</option>
                    {schedules.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="display-url">
                  <code>{displayUrl(display._id)}</code>
                  <button className="btn-copy" onClick={() => copyUrl(display._id)} title="URL kopieren">📋</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
